/**
 * client/src/audio/pageWindow.ts — which pages we are holding bytes for, and in what form.
 *
 * §5.7 splits the source strategy by **whether we have time**, and the split is not a preference:
 *
 *   - **Prefetched pages (N+1, N+2) and offline books: `fetch` → `Blob` → `objectURL`.** There are
 *     seconds to spare, and a blob starts from memory, needs no network, needs no Range, and asks no
 *     question about credential mode on a media element. That is what makes a page boundary silent
 *     with the screen locked on cellular — the bytes were already here before the screen went off.
 *   - **Cold open, and a seek to an unprimed page: the network URL, directly.** A blob for the page
 *     you are about to play means the whole ~380 KB must download before a single sample plays —
 *     ~3 s of dead air on a 1 Mbps link against ~300 ms with `+faststart` — and it nullifies the
 *     entire reason §4.4 chose MP4 with a front `moov` over anything else.
 *
 * Getting that backwards produces no error anywhere. It produces a play button that takes three
 * seconds to make a sound, which everybody experiences as "the app is slow" and nobody reports as a
 * bug, because there is nothing to report.
 *
 * ⚠️ **At most three primed blobs, revoked on eviction** (§5.7). A page is 300 KB–1.5 MB; an
 * `objectURL` that is never revoked pins its blob for the lifetime of the document, so a leak here
 * is a phone that gets slower the longer you read and dies somewhere in chapter nine. There is no
 * error, no console warning and no way to see it except a memory graph nobody is looking at.
 *
 * ⚠️ **LAW 14. The service worker must not claim `/api/v1/audio/`.** Nothing in this file installs
 * one, and the blob path bypasses one anyway — but a cache-first SW that returns a plain `200` for
 * a media element breaks Safari playback or plays once and refuses to seek, so the engine is built
 * to need no SW at all. When P6 writes one, the first line of its `fetch` handler is
 * `if (url.pathname.startsWith('/api/v1/audio/')) return;` — no `respondWith`.
 *
 * No React, no DOM, no globals: everything this file touches arrives through `BlobHost` so it can be
 * driven from `node --test` with a fake network. That is deliberate — the audio path is the part of
 * P2 that cannot be tested by looking at it.
 */

import type { PageAudio } from "../../../shared/narration/cue.ts";

// ─── what the engine needs to know about a page ──────────────────────────────

/**
 * A page, as the audio engine sees it: the artifact plus the **chapter** framing.
 *
 * The chapter fields are here rather than fetched separately because Media Session metadata is
 * chapter-scoped (§5.7) and `setPositionState` reports `chapterOffsetSec + el.currentTime` against
 * `chapterDurationSec`. A lock screen showing a 90-second track that restarts every page is the
 * difference between "an audiobook" and "a website that makes noise", and the only way to avoid it
 * is for whatever hands us a page to hand us its place in its chapter at the same time.
 *
 * `audio` has already been through `validatePageAudio()` (LAW 9) by the time it gets here. This file
 * never coerces a missing number, and neither does the engine.
 */
export interface EnginePage {
  /** The artifact and its cues. Validated — LAW 9 — before construction. */
  audio: PageAudio;
  /** Which chapter this page is in. `nexttrack` on a lock screen means CHAPTER, so we need it. */
  chapterOrd: number;
  /** What the lock screen says. Chapter title, never the page id. */
  chapterTitle: string;
  /** Seconds from the start of the chapter to this artifact's t=0. Chapter-scoped position needs it. */
  chapterOffsetSec: number;
  /** The whole chapter's length in seconds — the scrubber's span on the lock screen. */
  chapterDurationSec: number;
}

/** Two pages are the same *bytes* when they agree on both. A regenerated page is a different one. */
export const samePageArtifact = (a: EnginePage | null, b: EnginePage | null): boolean =>
  a !== null && b !== null && a.audio.pageId === b.audio.pageId && a.audio.artifactSha === b.audio.artifactSha;

/** The cache key for a primed blob. Content-addressed, so a re-narration can never hit a stale entry. */
export const primeKey = (p: EnginePage): string => `${p.audio.pageId}\0${p.audio.artifactSha}`;

// ─── the numbers ─────────────────────────────────────────────────────────────

/**
 * How many pages ahead we hold bytes for. §4.9's `AB_PREFETCH_PAGES=2` is the server's *generation*
 * window; this is the client's *delivery* window, and they are deliberately the same number for the
 * same reason: prime N+1 **and** N+2 before the screen can lock, so a hidden tab needs no network
 * for the next three minutes (~110 s of audio per page, measured — see the note on `plan.ts`).
 *
 * It is a local constant and not an import from `shared/config.ts` because that file is the server's
 * env parser; the client has no env, and a client that asks the server how many blobs to hold has
 * invented a round trip to answer a question about its own memory.
 */
export const PREFETCH_AHEAD = 2;

/** §5.7, literally: "at most three primed blobs". One playing, two ahead — `1 + PREFETCH_AHEAD`. */
export const MAX_PRIMED = 1 + PREFETCH_AHEAD;

/**
 * A page that claims to be bigger than this is not primed; it streams from the network instead.
 *
 * Measured delivery is 6.45–6.50 KB/s of AAC (this run, against the live stitch), so a 110-second
 * page is ~0.7 MB and the largest plausible page is a couple of megabytes. 8 MB is far above any real
 * page and far below "the phone died holding three of these". Without a ceiling, one corrupt or
 * mis-keyed artifact turns a prefetch into an out-of-memory crash on the device the product is for.
 */
export const MAX_PRIME_BYTES = 8 * 1024 * 1024;

// ─── the host: everything outside this file ──────────────────────────────────

/**
 * The part of `RequestInit` this client uses. A real `fetch` accepts it; a fake one can read it.
 *
 * `keepalive` is here for LAW 11's beat, which is sent from a page swap that may be the last thing
 * this document does before the phone freezes it.
 */
export interface HttpInit {
  method?: string;
  headers?: Record<string, string>;
  body?: string;
  credentials?: "same-origin" | "omit" | "include";
  keepalive?: boolean;
  signal?: AbortSignal;
}

/** The `URL.createObjectURL` half of the browser, injectable so the window is testable. */
export interface BlobHost {
  fetch(input: string, init?: HttpInit): Promise<BlobResponse>;
  createObjectURL(blob: BlobLike): string;
  revokeObjectURL(url: string): void;
  now(): number;
}

/** The part of `Response` we use. Structural, so a real `Response` satisfies it. */
export interface BlobResponse {
  readonly ok: boolean;
  readonly status: number;
  readonly headers: { get(name: string): string | null };
  blob(): Promise<BlobLike>;
  json(): Promise<unknown>;
}

/** The part of `Blob` we use. */
export interface BlobLike {
  readonly size: number;
  readonly type: string;
}

/** A blob we hold, and the URL that points at it. */
export interface Primed {
  pageId: string;
  artifactSha: string;
  /** Assign this to `<audio>.src`. It is revoked when the page leaves the window. */
  objectUrl: string;
  bytes: number;
  primedAt: number;
  usedAt: number;
}

/** Why a prime did not happen. Never thrown — the caller falls back to the network URL. */
export interface PrimeFailure {
  pageId: string;
  reason: "http" | "too-big" | "network" | "empty";
  status: number | null;
  detail: string;
}

// ─── the window ──────────────────────────────────────────────────────────────

/**
 * The set of pages whose bytes we are holding, and the only place an `objectURL` is created or
 * revoked.
 *
 * The invariant that matters, and the one a future edit will break: **a URL is revoked only when no
 * `<audio>` element still points at it.** `keep()` is how the engine states which pages are still
 * addressed — it must include the page assigned to *every* element, not just the one playing, because
 * the idle half of the ping-pong pair is holding N+1 precisely so that the swap costs nothing.
 * Revoking a URL an element still has assigned does not error; it makes the next `load()` fail with a
 * `MEDIA_ERR_SRC_NOT_SUPPORTED` several seconds later, on a page boundary, with the screen locked.
 */
export class PageWindow {
  private readonly primed = new Map<string, Primed>();
  private readonly inflight = new Map<string, Promise<Primed | null>>();
  private readonly failures: PrimeFailure[] = [];

  constructor(private readonly host: BlobHost) {}

  /** What we are holding, newest use first. Diagnostics and tests; never a control path. */
  held(): Primed[] {
    return [...this.primed.values()].sort((a, b) => b.usedAt - a.usedAt);
  }

  bytesHeld(): number {
    let n = 0;
    for (const p of this.primed.values()) n += p.bytes;
    return n;
  }

  /** The last few prime failures, for `/api/v1/status`-shaped reporting and for tests. */
  recentFailures(): readonly PrimeFailure[] {
    return this.failures;
  }

  /** Already in memory? Used to decide blob-vs-network without starting a fetch. */
  lookup(page: EnginePage): Primed | null {
    const hit = this.primed.get(primeKey(page));
    if (hit === undefined) return null;
    hit.usedAt = this.host.now();
    return hit;
  }

  /**
   * Fetch a page into memory and hand back an `objectURL`, or `null` if that did not work.
   *
   * **`null` is not an error path, it is the fallback path.** A prefetch that fails must degrade to
   * streaming the network URL at the boundary, not to silence: the bytes are still there, we just do
   * not have them early. Every reason is recorded rather than thrown, because the one thing this must
   * never do is reject into an `ended` handler and take the page turn down with it.
   */
  prime(page: EnginePage): Promise<Primed | null> {
    const key = primeKey(page);

    const hit = this.primed.get(key);
    if (hit !== undefined) {
      hit.usedAt = this.host.now();
      return Promise.resolve(hit);
    }

    // Two prefetch passes can name the same page — the window moves by one and N+2 becomes N+1 — and
    // fetching the same 700 KB twice on a cellular link is exactly the cost this file exists to avoid.
    const already = this.inflight.get(key);
    if (already !== undefined) return already;

    const run = this.fetchInto(page, key).finally(() => {
      this.inflight.delete(key);
    });
    this.inflight.set(key, run);
    return run;
  }

  private async fetchInto(page: EnginePage, key: string): Promise<Primed | null> {
    const url = page.audio.url;
    try {
      /**
       * `credentials: "same-origin"` and nothing else. The session is the signed httpOnly `abk`
       * cookie (§6.1) — there is no header to attach, and the audio route sets no cookie (LAW 15).
       * No `cache` override either: `?v=<artifactSha>` is a content hash and the route answers
       * `immutable`, so the HTTP cache is already correct and telling it otherwise would re-download
       * a page the browser is holding.
       */
      const res = await this.host.fetch(url, { credentials: "same-origin" });
      if (!res.ok) {
        this.note({ pageId: page.audio.pageId, reason: "http", status: res.status, detail: `${res.status} on ${url}` });
        return null;
      }

      // Checked before `.blob()` so an absurd artifact is refused before it is in memory, not after.
      const declared = Number(res.headers.get("content-length"));
      if (Number.isFinite(declared) && declared > MAX_PRIME_BYTES) {
        this.note({ pageId: page.audio.pageId, reason: "too-big", status: res.status, detail: `${declared} bytes declared` });
        return null;
      }

      const blob = await res.blob();
      if (blob.size === 0) {
        // A zero-length 200 is the shape a truncated push leaves behind (§4.4/LAW 8's whole point).
        // Streaming it would give the element an empty file and a page of silence with no error.
        this.note({ pageId: page.audio.pageId, reason: "empty", status: res.status, detail: "0 bytes" });
        return null;
      }
      if (blob.size > MAX_PRIME_BYTES) {
        this.note({ pageId: page.audio.pageId, reason: "too-big", status: res.status, detail: `${blob.size} bytes` });
        return null;
      }

      const now = this.host.now();
      const entry: Primed = {
        pageId: page.audio.pageId,
        artifactSha: page.audio.artifactSha,
        objectUrl: this.host.createObjectURL(blob),
        bytes: blob.size,
        primedAt: now,
        usedAt: now,
      };
      this.primed.set(key, entry);
      this.evictBeyondCap(key);
      return entry;
    } catch (err) {
      this.note({ pageId: page.audio.pageId, reason: "network", status: null, detail: String(err) });
      return null;
    }
  }

  /**
   * Keep exactly these pages and revoke everything else.
   *
   * The engine calls this after every swap with `[current, N+1, N+2]`. Passing a page id whose blob
   * is assigned to an element is the caller's job — see the class comment. Ids, not artifact shas:
   * a page that has been re-narrated while we held the old bytes should drop the old ones, and it
   * does, because the *key* carries the sha while this list does not.
   */
  keep(pageIds: readonly string[]): void {
    const live = new Set(pageIds);
    for (const [key, entry] of this.primed) {
      if (!live.has(entry.pageId)) this.drop(key);
    }
  }

  /** Drop one page's bytes, whatever artifact we hold for it. */
  release(pageId: string): void {
    for (const [key, entry] of this.primed) {
      if (entry.pageId === pageId) this.drop(key);
    }
  }

  /** Revoke everything. Called when the reader unmounts — a document teardown does not revoke for us. */
  clear(): void {
    for (const key of [...this.primed.keys()]) this.drop(key);
    this.failures.length = 0;
  }

  private drop(key: string): void {
    const entry = this.primed.get(key);
    if (entry === undefined) return;
    this.primed.delete(key);
    // Never let a revoke failure escape: this runs inside `ended` handlers and page swaps, and an
    // exception there costs the listener the rest of the book.
    try {
      this.host.revokeObjectURL(entry.objectUrl);
    } catch {
      /* a URL the host has already forgotten is not a problem worth a page turn */
    }
  }

  /**
   * The safety net under `keep()`, not a substitute for it: least-recently-used first, the entry we
   * just created last. `keep()` is authoritative because it knows which blobs an element is holding
   * and this does not — so this only ever fires if a caller forgot, and when it fires it prefers to
   * evict the oldest rather than to grow without limit.
   */
  private evictBeyondCap(justAdded: string): void {
    if (this.primed.size <= MAX_PRIMED) return;
    const byAge = [...this.primed.entries()]
      .filter(([key]) => key !== justAdded)
      .sort((a, b) => a[1].usedAt - b[1].usedAt);
    for (const [key] of byAge) {
      if (this.primed.size <= MAX_PRIMED) break;
      this.drop(key);
    }
  }

  private note(f: PrimeFailure): void {
    this.failures.push(f);
    if (this.failures.length > 8) this.failures.shift();
  }
}

// ─── choosing the source ─────────────────────────────────────────────────────

export type SourceKind = "blob" | "network";

export interface ChosenSource {
  url: string;
  kind: SourceKind;
}

/**
 * §5.7's split, as one function so there is one place it can be got wrong.
 *
 * Note what the split is actually about: **whether to spend time fetching**, not what to do with
 * bytes you already hold. A blob in hand is strictly better than the network for any load, urgent or
 * not — it starts from memory. So urgency decides whether the caller calls `prime()` *first*
 * (`primeIfUnhurried` below), and this function decides nothing except "do we have it".
 *
 * Written as one exported function anyway, because the alternative is `primed ? primed.objectUrl :
 * page.audio.url` inlined at four call sites in the engine, and the fifth one — added by whoever
 * implements offline books — is where it gets written the other way round.
 */
export function chooseSource(page: EnginePage, primed: Primed | null): ChosenSource {
  if (primed !== null) return { url: primed.objectUrl, kind: "blob" };
  return { url: page.audio.url, kind: "network" };
}

/**
 * Whether the network is known to be gone. Set by the offline runtime, read only by `primeIfUnhurried`.
 *
 * Module state rather than a `BlobHost` member on purpose: adding it to the host would change the
 * shape every fake host in `test/` constructs, to carry a fact none of them has an opinion about.
 * It is written from exactly one place (`client/src/lib/outbox.ts`, which owns the answer to "is the
 * network there?") and it defaults to `false`, so nothing that never calls the setter — every test,
 * and `node --test` in general — behaves any differently than before.
 */
let networkOffline = false;

export function setNetworkOffline(offline: boolean): void {
  networkOffline = offline;
}

/** For diagnostics and for the tests that pin the behaviour below. */
export const isNetworkOffline = (): boolean => networkOffline;

/**
 * Prime, unless a sample has to come out of the speaker as soon as possible.
 *
 * `urgent` is a cold open, a seek to an unprimed page, or a resume after waiting for the GPU. There,
 * `+faststart` gives ~300 ms to first sound and a full download gives ~3 s of dead air, so we take
 * the network URL and let the container do the job it was chosen for. Everything else — N+1, N+2, an
 * offline book — is prefetch, and at a page boundary the network is the thing most likely to be gone.
 *
 * ⚠️ **Urgency is a bet on the network, so with no network there is nothing to be urgent about.**
 * P6's whole gate is "the phone goes into airplane mode and it reads and plays a full chapter", and
 * on that phone the network URL is not 300 ms to first sound, it is a `MEDIA_ERR_NETWORK` several
 * seconds later with the reader staring at a play button that did nothing. `win.prime()` reads the
 * downloaded artifact out of `ab-audio-v1` through the host's `fetch` (see `browserEngineHost` in
 * AudioEngine.ts) and comes back with a blob in a few milliseconds, because the bytes are on the
 * device. The one case §5.7 wrote the split for — trading 3 s of download against 300 ms of
 * `+faststart` — simply does not arise when the download is a disk read.
 */
export function primeIfUnhurried(win: PageWindow, page: EnginePage, urgent: boolean): Promise<Primed | null> {
  const have = win.lookup(page);
  if (have !== null) return Promise.resolve(have);
  if (urgent && !networkOffline) return Promise.resolve(null);
  return win.prime(page);
}

/**
 * The page ids whose bytes should be in memory right now: the one playing, plus the ones ahead.
 *
 * Deduped and order-preserving. The current page is always first and is never evicted by the window
 * moving — a listener who pauses, walks away and comes back must not have to re-download the page
 * they are standing in.
 */
export function primeTargets(currentPageId: string, ahead: readonly string[], n: number = PREFETCH_AHEAD): string[] {
  const out: string[] = [currentPageId];
  for (const id of ahead) {
    if (out.length > n) break;
    if (!out.includes(id)) out.push(id);
  }
  return out;
}

/** `chooseSource` never returns a blob for an urgent load unless we already have one in hand. */
export const isBlobUrl = (url: string): boolean => url.startsWith("blob:");
